import { useMemo, useState } from 'react'

import MailIcon from '@heroicons/react/outline/MailIcon'
import PencilAltIcon from '@heroicons/react/outline/PencilAltIcon'
import PhotographIcon from '@heroicons/react/outline/PhotographIcon'
import ShieldExclamationIcon from '@heroicons/react/outline/ShieldExclamationIcon'
import TrashIcon from '@heroicons/react/outline/TrashIcon'
import UserIcon from '@heroicons/react/outline/UserIcon'
import { useDecision } from '@optimizely/react-sdk'
import { Link } from 'react-router-dom'

import {
  CustomFieldType,
  CustomFieldValidators,
  Member,
  MemberStatus,
} from '@tribeplatform/gql-client/types'
import {
  DefaultMembersStatus,
  useAuthToken,
  useDeleteMember,
  useMembers,
  useSendResetPasswordEmail,
  useSpaceUtil,
} from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { confirm } from '@tribeplatform/react-ui-kit/Dialog'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { FeaturesList } from '../../../lib/optimizely/feature.list'
import { useDebounce } from '../../../utils/useDebounce'
import { AuditLogsModal } from '../../AuditLog/AuditLogsModal'
import { CustomFieldSubtype, getFieldSetting } from '../../CustomField/utils'
import { ProfileSettingsModal } from '../../Settings/ProfileSettingsModal'
import { Table } from '../../Table'
import { UserImportModal } from '../../UserImport'

export const Members = () => {
  const { generateMemberPath } = useSpaceUtil()
  const [editMemberModal, setEditMemberModal] = useState<string>()
  const [inviteMembersModal, setInviteMembersModal] = useState(false)
  const [auditLogsModal, setAuditLogsModal] = useState<string>()
  const [filters, setFilters] = useState({ query: undefined, filterBy: [] })
  const [orderBy, setOrderBy] = useState({
    column: 'createdAt',
    reverse: true,
  })
  const {
    data: { network, member: authMember },
  } = useAuthToken()
  const debouncedQuery = useDebounce(filters?.query, 300)

  const [decisionAuditLogs] = useDecision(FeaturesList.NewModerationPanel)

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useMembers({
      fields: {
        profilePicture: 'basic',
        banner: 'basic',
        fields: 'all',
        role: 'basic',
      },
      variables: {
        limit: 15,
        query: debouncedQuery,
        orderBy: orderBy.column,
        reverse: orderBy.reverse,
        status: DefaultMembersStatus,
        filterBy: filters?.filterBy || [],
      },
      useInfiniteQueryOptions: { keepPreviousData: true },
    })

  const { mutateAsync: sendResetPasswordEmail } = useSendResetPasswordEmail()
  const { mutateAsync: deleteMember } = useDeleteMember()

  const members = simplifyPaginatedResult<Member>(data)
  members?.nodes?.forEach(member => {
    /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
    // @ts-ignore
    member.mappedFields = member?.fields?.reduce((result, cur) => {
      try {
        result[cur.key] = JSON.parse(cur.value)
      } catch (e) {
        logger.error('Error parsing member field value', e.message)
      }
      return result
    }, {})
  })

  const onDelete = async (member: Member) => {
    if (!member?.id) return

    const confirmed = await confirm({
      title: `Are you sure you want to remove ${member?.name} (${member?.username})?`,
      proceedLabel: 'Remove',
      danger: true,
    })
    if (!confirmed) {
      return
    }

    try {
      await deleteMember({ id: member?.id })
      toast({
        title: 'Member removed',
        description: 'Member is successfully removed.',
        status: 'success',
      })
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: 'Cannot remove this member.',
        status: 'error',
      })
    }
  }

  const onSendresetPasswordEmail = async (member: Member) => {
    if (!member?.id) return

    try {
      await sendResetPasswordEmail({ email: member?.email })
      toast({
        title: 'Email sent',
        description: 'Reset password email sent to the member.',
        status: 'success',
      })
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: 'Cannot send reset password email.',
        status: 'error',
      })
    }
  }

  const customColumns = []
  network?.memberFields?.fields?.forEach(field => {
    if (field.archived || field.type === CustomFieldType.RICH_TEXT) return
    customColumns.push({
      accessor: `mappedFields.${field.key}`,
      Header: field.name,
      orderKey: `fields.${field.key}`,
      type: field.type,
      renderer: field.type,
      validators: field.validators,
      subtype: getFieldSetting(field, 'subtype'),
    })
  })

  const columns = useMemo(
    () => [
      {
        name: 'Profile Picture',
        Header: (
          <div className="w-8">
            <PhotographIcon className="w-5 h-5 m-auto" />
          </div>
        ),
        accessor: 'profilePicture.url',
        renderer: 'avatar',
        visible: true,
        width: 'collapse',
      },
      {
        Header: 'ID',
        accessor: 'id',
        renderer: 'memberPrimary',
        visible: false,
        width: 'sm',
      },
      {
        Header: 'Name',
        accessor: 'name',
        renderer: 'memberPrimary',
        visible: true,
        width: 'md',
        type: CustomFieldType.TEXT,
      },
      {
        Header: 'Username',
        accessor: 'username',
        renderer: 'memberPrimary',
        width: 'md',
        visible: true,
        type: CustomFieldType.TEXT,
      },
      {
        Header: 'Email',
        accessor: 'email',
        renderer: 'email',
        width: 'lg',
        visible: true,
        type: CustomFieldType.TEXT,
        subtype: CustomFieldSubtype.EMAIL,
      },
      {
        Header: 'Tagline',
        accessor: 'tagline',
        width: 'md',
        visible: false,
      },
      {
        Header: 'Role',
        accessor: 'role.name',
        width: 'sm',
        visible: false,
        renderer: (member: Member) => {
          return member?.status === MemberStatus.UNVERIFIED
            ? 'Unverified'
            : member?.['role.name']
        },
      },
      {
        Header: 'Created at',
        accessor: 'createdAt',
        renderer: 'date',
        orderKey: 'createdAt',
        width: 'sm',
        visible: true,
        type: CustomFieldType.DATE,
      },
      {
        Header: 'Updated at',
        accessor: 'updatedAt',
        orderKey: 'updatedAt',
        renderer: 'date',
        width: 'sm',
        visible: true,
        type: CustomFieldType.DATE,
      },
      {
        Header: 'Last seen',
        accessor: 'lastSeenAt',
        orderKey: 'lastSeenAt',
        renderer: 'date',
        format: 'fromNow',
        type: CustomFieldType.DATE,
        width: 'sm',
        visible: true,
      },
      {
        Header: 'Email Status',
        accessor: 'status',
        width: 'sm',
        subtype: CustomFieldSubtype.SELECT,
        type: CustomFieldType.TEXT,
        validators: [
          {
            validation: CustomFieldValidators.ENUM,
            value: JSON.stringify(['VERIFIED', 'UNVERIFIED']),
          },
        ],
      },
      {
        Header: 'Locale',
        accessor: 'locale',
        width: 'sm',
      },
      ...customColumns,
      {
        visible: true,
        accessor: 'actions',
        locked: true,
        width: 'xs',
        renderer: member => (
          <Dropdown>
            <Dropdown.ButtonMinimal />
            <Dropdown.Items>
              <Dropdown.Item
                as={Link}
                to={generateMemberPath(member)}
                leadingIcon={<UserIcon />}
              >
                View profile
              </Dropdown.Item>
              <Dropdown.Item
                leadingIcon={<PencilAltIcon />}
                onClick={() => {
                  setEditMemberModal(member?.id)
                }}
              >
                Edit member
              </Dropdown.Item>
              <Dropdown.Item
                leadingIcon={<MailIcon />}
                onClick={() => onSendresetPasswordEmail(member)}
              >
                Reset password
              </Dropdown.Item>
              {decisionAuditLogs?.enabled && (
                <Dropdown.Item
                  leadingIcon={<ShieldExclamationIcon />}
                  onClick={() => setAuditLogsModal(member?.id)}
                >
                  Audit log
                </Dropdown.Item>
              )}
              <Dropdown.Item
                leadingIcon={<TrashIcon />}
                onClick={() => onDelete(member)}
                disabled={authMember?.id === member?.id}
              >
                Remove member
              </Dropdown.Item>
            </Dropdown.Items>
          </Dropdown>
        ),
      },
    ],
    [],
  )

  return (
    <div className="w-full">
      <Table
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        columns={columns}
        data={data}
        setFilters={setFilters}
        filters={filters}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
        title="Members"
        actions={
          <>
            <Button
              size="lg"
              variant="primary"
              onClick={() => setInviteMembersModal(true)}
            >
              Invite members
            </Button>
          </>
        }
      />
      {editMemberModal ? (
        <ProfileSettingsModal
          member={members?.nodes?.find(m => m.id === editMemberModal)}
          open
          onClose={() => setEditMemberModal(null)}
        />
      ) : null}
      {inviteMembersModal ? (
        <UserImportModal open onClose={() => setInviteMembersModal(false)} />
      ) : null}
      {auditLogsModal && (
        <AuditLogsModal
          open
          onClose={() => setAuditLogsModal(null)}
          memberId={auditLogsModal}
        />
      )}
    </div>
  )
}
